import {
  AfterContentInit,
  ContentChild,
  Directive,
  ElementRef,
  HostBinding,
  effect,
  inject,
  input,
  Renderer2,
  RendererStyleFlags2,
  NgZone,
  OnDestroy,
  OnInit,
  untracked,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { throttle } from 'lodash-es';
import { ScreenSizeService } from 'src/app/core/services/screen-size.service';
import { FormPageHeadingComponent } from '../components/form-page-heading/form-page-heading.component';
import { MobileDetailViewShellService } from '../services/mobile-detail-view-shell.service';
import { FORM_PAGE_HEADER_MODE, FormPageHeadingMode } from './form-page-header-mode';

const DEFAULT_THROTTLE_TIME = 150;
const SCROLL_THROTTLE_TIME = 80;
const HEADING_BUTTONS_GAP = 16;
const COMPACT_SCROLL_THRESHOLD = 16;
const COMPACT_SCROLL_EXIT_THRESHOLD = 2;
const SCROLL_DIRECTION_THRESHOLD = 4;
const SCROLL_INPUT_INTENT_TTL_MS = 500;
const COMPACT_LAYOUT_SHIFT_IGNORE_MS = 500;
const COMPACT_LAYOUT_SHIFT_MIN = 16;
const COMPACT_LAYOUT_SHIFT_MAX = 240;
const CONTENT_SHRINK_IGNORE_MS = 450;
const COMPACT_TITLE_INLINE_GAP = 12;

type ScrollInputDirection = 'down' | 'up';

@Directive({
  selector: '[appFormPageHeader]',
  exportAs: 'formPageHeader',
  standalone: true,
  providers: [
    {
      provide: FORM_PAGE_HEADER_MODE,
      useFactory: () => inject(FormPageHeaderDirective).detailViewMode,
    },
  ],
})
export class FormPageHeaderDirective implements AfterContentInit, OnInit, OnDestroy {
  private readonly screenSizeService = inject(ScreenSizeService);
  private readonly isMobile = toSignal(this.screenSizeService.isMobile$, {
    initialValue: this.screenSizeService.isMobileSubject.value,
  });

  readonly embedded = input(false);
  readonly detailViewMode = input<FormPageHeadingMode | null>(null);

  @HostBinding('class.form-page-header--desktop-detail') get isDesktopLayout(): boolean {
    return !this.embedded() && !this.isMobile();
  }

  @ContentChild(FormPageHeadingComponent, { read: ElementRef, descendants: true })
  formPageHeading: ElementRef<HTMLElement>;

  @HostBinding('class.form-page-header--mobile-detail') get isMobileLayout(): boolean {
    return !this.embedded() && this.isMobile();
  }

  @HostBinding('class.form-page-header--embedded') get embeddedClass(): boolean {
    return this.embedded();
  }
  @HostBinding('class.form-page-header--compact') compactClass = false;

  private readonly headerId: number;
  private previousScrollTop = 0;
  private previousMaxScrollTop = 0;
  private scrollingDown = false;
  private compactedAt = 0;
  private contentShrinkIgnoreUntil = 0;
  private scrollInputDirection: ScrollInputDirection | null = null;
  private scrollInputAt = 0;
  private touchY?: number;
  private scrollTargets: (HTMLElement | Window)[] = [];
  private scrollUnlisteners: (() => void)[] = [];
  private inputUnlisteners: (() => void)[] = [];
  private resizeUnlistener?: () => void;
  private handler = throttle(() => this.zone.run(() => this.onResize()), DEFAULT_THROTTLE_TIME);
  private scrollHandler = throttle(
    () => this.zone.run(() => this.onScroll()),
    SCROLL_THROTTLE_TIME
  );
  private headerResizeObserver = new ResizeObserver(this.handler);
  private headerMutationObserver = new MutationObserver(this.handler);
  private contentResizeObserver = new ResizeObserver(() =>
    this.zone.run(() => this.onContentResize())
  );

  resize(): void {
    if (!this.formPageHeading?.nativeElement) {
      return;
    }

    this.updateCompactTitleInset();
    this.updateDesktopTabsOffset();

    if (!this.embedded() || this.isMobile()) {
      this.renderer.setStyle(this.formPageHeading.nativeElement, 'max-width', '100%');
      return;
    }

    const headerWidth = this.elementRef.nativeElement.offsetWidth;
    const buttonsContainer =
      this.elementRef.nativeElement.querySelector<HTMLElement>('.buttons-container');
    const buttonsContainerWidth = buttonsContainer?.offsetWidth || 0;
    const headerPaddingRight =
      parseFloat(getComputedStyle(this.elementRef.nativeElement).paddingRight) || 0;
    const availableSpace =
      headerWidth - buttonsContainerWidth - headerPaddingRight - HEADING_BUTTONS_GAP;

    this.renderer.setStyle(
      this.formPageHeading.nativeElement,
      'max-width',
      availableSpace > 0 ? `${availableSpace}px` : '100%'
    );
  }

  constructor(
    private elementRef: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    private zone: NgZone,
    private mobileDetailViewShell: MobileDetailViewShellService
  ) {
    this.headerId = this.mobileDetailViewShell.createHeaderId();
    effect(() => {
      this.isMobile();
      this.embedded();
      untracked(() => this.onResize());
    });
  }

  ngAfterContentInit() {
    this.resize();
    this.bindScrollTargets();
    this.bindScrollIntentListeners();
    this.syncHeaderState();
  }

  ngOnInit(): void {
    this.headerResizeObserver.observe(this.elementRef.nativeElement);
    this.headerMutationObserver.observe(this.elementRef.nativeElement, {
      childList: true,
      subtree: true,
    });
    this.resizeUnlistener = this.renderer.listen('window', 'resize', this.handler);
    this.mobileDetailViewShell.register(this.headerId, {
      compact: false,
      mobile: this.isMobileLayout,
      scrollingDown: false,
    });
  }

  ngOnDestroy(): void {
    this.scrollUnlisteners.forEach((unlisten) => unlisten());
    this.inputUnlisteners.forEach((unlisten) => unlisten());
    this.resizeUnlistener?.();
    this.headerResizeObserver.disconnect();
    this.headerMutationObserver.disconnect();
    this.contentResizeObserver.disconnect();
    this.handler.cancel();
    this.scrollHandler.cancel();
    this.mobileDetailViewShell.unregister(this.headerId);
  }

  private onResize(): void {
    this.resize();
    this.syncHeaderState();
  }

  private onContentResize(): void {
    const maxScrollTop = this.getMaxScrollTop();
    const maxScrollDelta = maxScrollTop - this.previousMaxScrollTop;
    if (maxScrollDelta < -SCROLL_DIRECTION_THRESHOLD) {
      this.markContentShrink();
    }
    this.previousMaxScrollTop = maxScrollTop;
  }

  private bindScrollTargets(): void {
    this.scrollUnlisteners.forEach((unlisten) => unlisten());
    this.contentResizeObserver.disconnect();
    this.scrollTargets = this.getScrollTargets();
    this.previousScrollTop = this.getScrollTop();
    this.previousMaxScrollTop = this.getMaxScrollTop();
    this.scrollUnlisteners = this.scrollTargets.map((target) =>
      this.renderer.listen(target, 'scroll', () => this.scrollHandler())
    );
    this.observeContentResizeTargets();
  }

  private observeContentResizeTargets(): void {
    this.scrollTargets.forEach((target) => {
      if (target instanceof HTMLElement) {
        this.contentResizeObserver.observe(target);
      }
    });

    const formCards = this.elementRef.nativeElement.nextElementSibling;
    if (formCards instanceof HTMLElement) {
      this.contentResizeObserver.observe(formCards);
    }
  }

  private getScrollTargets(): (HTMLElement | Window)[] {
    const header = this.elementRef.nativeElement;
    const targets: (HTMLElement | Window)[] = [];
    const formCards = header.nextElementSibling;
    const innerContent = header.closest('.inner-sidenav-content');

    if (formCards instanceof HTMLElement) {
      targets.push(formCards);
    }
    if (innerContent instanceof HTMLElement && innerContent !== formCards) {
      targets.push(innerContent);
    }
    targets.push(window);
    return targets;
  }

  private onScroll(): void {
    const scrollTop = this.getScrollTop();
    const maxScrollTop = this.getMaxScrollTop();
    const scrollDelta = scrollTop - this.previousScrollTop;
    const maxScrollDelta = maxScrollTop - this.previousMaxScrollTop;

    if (maxScrollDelta < -SCROLL_DIRECTION_THRESHOLD) {
      this.markContentShrink();
    }

    this.previousScrollTop = scrollTop;
    this.previousMaxScrollTop = maxScrollTop;
    this.syncHeaderState(scrollDelta);
  }

  private syncHeaderState(scrollDelta?: number): void {
    const mobile = this.isMobileLayout;
    const scrollTop = this.getScrollTop();
    const compact = this.shouldUseCompactLayout(mobile || this.isDesktopLayout, scrollTop);
    const wasCompact = this.compactClass;

    if (!mobile || !compact) {
      this.scrollingDown = false;
      this.compactedAt = 0;
    } else if (scrollDelta !== undefined && scrollDelta > SCROLL_DIRECTION_THRESHOLD) {
      this.scrollingDown = true;
      if (!wasCompact) {
        this.compactedAt = Date.now();
      }
    } else if (this.isRecentScrollInputDirection('down')) {
      this.scrollingDown = true;
      if (!wasCompact) {
        this.compactedAt = Date.now();
      }
    } else if (scrollDelta !== undefined && scrollDelta < -SCROLL_DIRECTION_THRESHOLD) {
      this.handleScrollUpDelta(scrollDelta);
    }

    this.compactClass = compact;
    this.mobileDetailViewShell.update(this.headerId, {
      compact,
      mobile,
      scrollingDown: this.scrollingDown,
    });
  }

  private handleScrollUpDelta(scrollDelta: number): void {
    if (this.isCompactLayoutShift(scrollDelta) || this.isContentShrinkIgnoreActive()) {
      this.compactedAt = Date.now();
      return;
    }

    this.scrollingDown = false;
  }

  private getScrollTop(): number {
    if (!this.scrollTargets.length) {
      return 0;
    }
    return Math.max(...this.scrollTargets.map((target) => this.getTargetScrollTop(target)));
  }

  private getMaxScrollTop(): number {
    if (!this.scrollTargets.length) {
      return 0;
    }
    return Math.max(...this.scrollTargets.map((target) => this.getTargetMaxScrollTop(target)));
  }

  private shouldUseCompactLayout(enabled: boolean, scrollTop: number): boolean {
    if (!enabled) {
      return false;
    }

    if (this.compactClass) {
      if (this.isMobileLayout && this.isContentShrinkIgnoreActive()) {
        return true;
      }
      return (
        scrollTop > COMPACT_SCROLL_EXIT_THRESHOLD ||
        (this.isMobileLayout && this.isRecentScrollInputDirection('down'))
      );
    }

    // Wait until the title has scrolled past its expanded height so shrinking it cannot
    // anchor a lightly scrolled desktop page back to zero or compact a short page.
    const threshold = this.isDesktopLayout
      ? Math.max(COMPACT_SCROLL_THRESHOLD, this.formPageHeading?.nativeElement.offsetHeight ?? 0)
      : COMPACT_SCROLL_THRESHOLD;
    return scrollTop > threshold;
  }

  private markContentShrink(): void {
    this.contentShrinkIgnoreUntil = Date.now() + CONTENT_SHRINK_IGNORE_MS;
  }

  private isContentShrinkIgnoreActive(): boolean {
    return Date.now() < this.contentShrinkIgnoreUntil;
  }

  private bindScrollIntentListeners(): void {
    this.inputUnlisteners = [
      this.renderer.listen('window', 'wheel', (event: WheelEvent) => {
        this.recordWheelScrollIntent(event);
      }),
      this.renderer.listen('window', 'touchstart', (event: TouchEvent) => {
        this.touchY = event.touches[0]?.clientY;
      }),
      this.renderer.listen('window', 'touchmove', (event: TouchEvent) => {
        this.recordTouchScrollIntent(event);
      }),
      this.renderer.listen('window', 'touchend', () => {
        this.touchY = undefined;
      }),
      this.renderer.listen('window', 'touchcancel', () => {
        this.touchY = undefined;
      }),
    ];
  }

  private recordWheelScrollIntent(event: WheelEvent): void {
    if (!this.isMobileLayout || Math.abs(event.deltaY) <= Math.abs(event.deltaX)) {
      return;
    }

    if (Math.abs(event.deltaY) <= SCROLL_DIRECTION_THRESHOLD) {
      return;
    }

    this.recordScrollInputDirection(event.deltaY > 0 ? 'down' : 'up');
  }

  private recordTouchScrollIntent(event: TouchEvent): void {
    const nextTouchY = event.touches[0]?.clientY;
    if (!this.isMobileLayout || nextTouchY === undefined) {
      this.touchY = nextTouchY;
      return;
    }

    if (this.touchY === undefined) {
      this.touchY = nextTouchY;
      return;
    }

    const scrollDelta = this.touchY - nextTouchY;
    this.touchY = nextTouchY;
    if (Math.abs(scrollDelta) <= SCROLL_DIRECTION_THRESHOLD) {
      return;
    }

    this.recordScrollInputDirection(scrollDelta > 0 ? 'down' : 'up');
  }

  private recordScrollInputDirection(direction: ScrollInputDirection): void {
    this.scrollInputDirection = direction;
    this.scrollInputAt = Date.now();

    if (
      direction === 'up' &&
      this.compactClass &&
      this.getScrollTop() <= COMPACT_SCROLL_EXIT_THRESHOLD
    ) {
      this.syncHeaderState();
    }
  }

  private isRecentScrollInputDirection(direction: ScrollInputDirection): boolean {
    return (
      this.scrollInputDirection === direction &&
      Date.now() - this.scrollInputAt <= SCROLL_INPUT_INTENT_TTL_MS
    );
  }

  private isCompactLayoutShift(scrollDelta: number): boolean {
    const elapsed = Date.now() - this.compactedAt;
    const scrollDeltaSize = Math.abs(scrollDelta);

    return (
      this.compactedAt > 0 &&
      elapsed <= COMPACT_LAYOUT_SHIFT_IGNORE_MS &&
      scrollDeltaSize >= COMPACT_LAYOUT_SHIFT_MIN &&
      scrollDeltaSize <= COMPACT_LAYOUT_SHIFT_MAX
    );
  }

  private getTargetScrollTop(target: HTMLElement | Window): number {
    if (target === window) {
      return this.clampScrollTop(window.scrollY, this.getWindowMaxScrollTop());
    }

    const element = target as HTMLElement;
    return this.clampScrollTop(element.scrollTop, element.scrollHeight - element.clientHeight);
  }

  private getTargetMaxScrollTop(target: HTMLElement | Window): number {
    if (target === window) {
      return Math.max(this.getWindowMaxScrollTop(), 0);
    }

    const element = target as HTMLElement;
    return Math.max(element.scrollHeight - element.clientHeight, 0);
  }

  private clampScrollTop(scrollTop: number, maxScrollTop: number): number {
    return Math.min(Math.max(scrollTop, 0), Math.max(maxScrollTop, 0));
  }

  private getWindowMaxScrollTop(): number {
    const documentElement = document.documentElement;
    const body = document.body;
    return Math.max(
      documentElement.scrollHeight - window.innerHeight,
      body.scrollHeight - window.innerHeight
    );
  }

  private updateDesktopTabsOffset(): void {
    const header = this.elementRef.nativeElement;
    const content = header.nextElementSibling;
    if (!this.isDesktopLayout || !(content instanceof HTMLElement)) {
      return;
    }
    this.renderer.setStyle(
      content,
      '--desktop-detail-header-height',
      `${header.offsetHeight}px`,
      RendererStyleFlags2.DashCase
    );
  }

  private updateCompactTitleInset(): void {
    const header = this.elementRef.nativeElement;
    const navLeadingAction = header.querySelector<HTMLElement>(
      '.detail-view-actions-bar__nav-leading app-ui-button'
    );
    const actionRowActions = header.querySelectorAll<HTMLElement>(
      '.detail-view-actions-bar__action-row app-ui-button'
    );
    // Prefer Back/Cancel when present; fall back to action-row for nav-less headers.
    const anchor = navLeadingAction ?? actionRowActions.item(actionRowActions.length - 1);

    if (!anchor) {
      this.renderer.removeStyle(
        header,
        '--mobile-detail-compact-title-left',
        RendererStyleFlags2.DashCase
      );
      return;
    }

    const titleLeft =
      anchor.getBoundingClientRect().right -
      header.getBoundingClientRect().left +
      COMPACT_TITLE_INLINE_GAP;

    this.renderer.setStyle(
      header,
      '--mobile-detail-compact-title-left',
      `${titleLeft}px`,
      RendererStyleFlags2.DashCase
    );
  }
}
